/**
 * Build-time settings, written into .env.local from Terraform outputs by `make web-config`.
 *
 * None of these are secrets. The Cognito app client is public by design — a browser cannot
 * keep a secret — and the API rejects any request without a token it has verified itself.
 */

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `${name} is not set. Run \`make web-config\` to write web/.env.local from the ` +
        `Terraform outputs, then rebuild.`,
    );
  }
  return value.replace(/\/+$/, "");
}

export const config = {
  apiUrl: required("VITE_API_URL", import.meta.env.VITE_API_URL),
  cognitoDomain: required("VITE_COGNITO_DOMAIN", import.meta.env.VITE_COGNITO_DOMAIN),
  clientId: required("VITE_COGNITO_CLIENT_ID", import.meta.env.VITE_COGNITO_CLIENT_ID),
  /** Must match a callback URL registered on the app client, exactly. */
  redirectUri: window.location.origin,
};

/** UK dates everywhere she can see. ISO-8601 UTC stays on the wire. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** The month keys the log view offers, newest first, back to the start of her workbook. */
export function monthOptions(): string[] {
  const months: string[] = [];
  const now = new Date();
  const cursor = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const earliest = Date.UTC(2025, 10, 1); // November 2025, where her log starts
  while (cursor.getTime() >= earliest) {
    months.push(cursor.toISOString().slice(0, 7));
    cursor.setUTCMonth(cursor.getUTCMonth() - 1);
  }
  return months;
}

export function monthLabel(monthKey: string): string {
  const [year, month] = monthKey.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
}
